import {
  PostEditorReducerFunction,
  PostEditorTextAddAction,
  PostEditorTextDropDoneAction,
  PostEditorTextRemoveAction,
  PostEditorTextReorderAction,
  PostEditorTextUpdateAction
} from '../PostEditorActions';
import { ImmutabilityContext } from '../effects/customImmutabilityContext';
import { OnStateUpdateCallbackFunction, postGroupStateUpdater } from '../../helpers/postGroupStateUpdater';
import { cloneDeep } from 'lodash';
import { GroupPostAttributesType, VariationMeta } from '../../../../models/post-editor';
import { PostEditorErrorKeys } from '../../../../models/validator-props';
import { calculateVariations } from '../../../../helpers/calculateVariations';
import { validatorStateUpdater } from '../../validators/helpers/validatorStateUpdater';
import { ErrorMessageSeverity } from '../../../../constants/form';
import { serializeText } from '../../TextEditor/helpers/serializer';
import { showToast } from '../../../../helpers/showToast';

const cardinalErrors: PostEditorErrorKeys[] = [
  'checkLinkPreviewSuppressed',
  'checkLinksPresent',
  'facebookContentRequired',
  'facebookTextCharLimit',
  'facebookTwitterLineBreakLimit',
  'googleTextCharLimit',
  'instagramHashtagLimit',
  'instagramTextCharLimit',
  'linkedinTextCharLimit',
  'mediaRequired',
  'pinterestDuplicatesCheck',
  'pinterestLineBreakLimit',
  'pinterestTextCharLimit',
  'preventTwDuplicate',
  'scheduleTextOrMediaVariations',
  'tikTokTextCharLimit',
  'twitterTextCharLimit'
];

const getVariationsData = (
  currentPost: GroupPostAttributesType
): Omit<VariationMeta, 'prevPostedCount' | 'currentVariationIndex'> => {
  const {
    varyMedia,
    variationMeta: { currentVariationIndex }
  } = currentPost.content;
  const { total, mediaVariationIndex, textVariationIndex } = calculateVariations(
    currentPost.content.texts,
    currentPost.content.media,
    varyMedia,
    currentVariationIndex
  );
  return { total, mediaVariationIndex, textVariationIndex };
};

export const textAddReducer: PostEditorReducerFunction<PostEditorTextAddAction['payload']> = (state, payload) => {
  const { postGroupIndex, customizeIndex, currentId } = payload;
  const callback: OnStateUpdateCallbackFunction = currentPost => {
    const { update: updateImmutable } = ImmutabilityContext;
    const index = currentPost.content.texts.findIndex(item => item.id === currentId);
    if (index < 0) {
      throw new Error('Current text index cound not be found!');
    }
    const text = cloneDeep(currentPost.content.texts[index].text);
    const id = Math.max(...currentPost.content.texts.map(value => value.id)) + 1;
    const notYetAdded = !currentPost.content.texts.find(text => text.id === id);
    if (notYetAdded) {
      const texts = updateImmutable(currentPost, {
        content: {
          texts: {
            $splice: [[index + 1, 0, { text, id }]]
          }
        }
      });
      const { total, mediaVariationIndex, textVariationIndex } = getVariationsData(texts);
      return updateImmutable(texts, {
        content: {
          variationMeta: {
            total: { $set: total },
            mediaVariationIndex: { $set: mediaVariationIndex },
            textVariationIndex: { $set: textVariationIndex }
          }
        }
      });
    }
    return currentPost;
  };
  const result = postGroupStateUpdater(state, postGroupIndex, customizeIndex, 'content', callback);
  return validatorStateUpdater(result, postGroupIndex, ErrorMessageSeverity.warning, ['profileRequired']);
};

export const textDropDoneReducer: PostEditorReducerFunction<PostEditorTextDropDoneAction['payload']> = (
  state,
  payload
) => {
  const { update: updateImmutable } = ImmutabilityContext;
  const { postGroupIndex, customizeIndex, texts } = payload;
  const callback: OnStateUpdateCallbackFunction = currentPost =>
    updateImmutable(currentPost, {
      content: {
        texts: { $set: texts }
      }
    });
  const result = postGroupStateUpdater(state, postGroupIndex, customizeIndex, 'content', callback);
  return validatorStateUpdater(result, postGroupIndex, ErrorMessageSeverity.warning, [
    ...cardinalErrors,
    'profileRequired'
  ]);
};

export const textReorderReducer: PostEditorReducerFunction<PostEditorTextReorderAction['payload']> = (
  state,
  payload
) => {
  const { postGroupIndex, customizeIndex, dragIndex, targetIndex } = payload;
  const callback: OnStateUpdateCallbackFunction = currentPost => {
    if (
      typeof currentPost.content.texts[dragIndex] !== 'undefined' &&
      typeof currentPost.content.texts[targetIndex] !== 'undefined'
    ) {
      const { update: updateImmutable } = ImmutabilityContext;
      return updateImmutable(currentPost, {
        content: {
          texts: {
            $splice: [
              [dragIndex, 1],
              [targetIndex, 0, currentPost.content.texts[dragIndex]]
            ]
          }
        }
      });
    }
    return currentPost;
  };
  const result = postGroupStateUpdater(state, postGroupIndex, customizeIndex, 'content', callback);
  return validatorStateUpdater(result, postGroupIndex, ErrorMessageSeverity.warning, [
    ...cardinalErrors,
    'profileRequired'
  ]);
};

export const textUpdateReducer: PostEditorReducerFunction<PostEditorTextUpdateAction['payload']> = (state, payload) => {
  const { postGroupIndex, customizeIndex, text, id, link } = payload;
  let currentText = '';
  const callback: OnStateUpdateCallbackFunction = currentPost => {
    const { update: updateImmutable } = ImmutabilityContext;
    const index = currentPost.content.texts.findIndex(item => item.id === id);
    currentText = currentPost.content.texts[index] ? serializeText(currentPost.content.texts[index].text) : currentText;
    return updateImmutable<GroupPostAttributesType>(currentPost, {
      content: {
        link: {
          // we only want to update current link if link has been passed in payload and is different from the current one
          $apply: current => (link !== undefined && JSON.stringify(link) !== JSON.stringify(current) ? link : current)
        },
        texts: {
          [index]: { $set: { text, id } }
        }
      }
    });
  };
  const result = postGroupStateUpdater(state, postGroupIndex, customizeIndex, 'content', callback);
  const textChanged = serializeText(text) !== currentText;
  const errorsList: PostEditorErrorKeys[] = textChanged ? [...cardinalErrors, 'profileRequired'] : cardinalErrors;
  return validatorStateUpdater(result, postGroupIndex, ErrorMessageSeverity.warning, errorsList);
};

export const textRemoveReducer: PostEditorReducerFunction<PostEditorTextRemoveAction['payload']> = (state, payload) => {
  const { postGroupIndex, customizeIndex, textId } = payload;
  const callback: OnStateUpdateCallbackFunction = currentPost => {
    const { update: updateImmutable } = ImmutabilityContext;
    const currentIndex = currentPost.content.texts.findIndex(text => text.id === textId);
    if (currentIndex < 0) {
      showToast({
        severity: 'error',
        summary: 'Delete Variation',
        detail: 'Failed to delete text variation. please try again.'
      });
      return currentPost;
    }
    const texts = updateImmutable(currentPost, {
      content: {
        texts: {
          $splice: [[currentIndex, 1]]
        }
      }
    });
    const { total, mediaVariationIndex, textVariationIndex } = getVariationsData(texts);
    return updateImmutable(texts, {
      content: {
        variationMeta: {
          total: { $set: total },
          mediaVariationIndex: { $set: mediaVariationIndex },
          textVariationIndex: { $set: textVariationIndex }
        }
      }
    });
  };
  const result = postGroupStateUpdater(state, postGroupIndex, customizeIndex, 'content', callback);
  return validatorStateUpdater(result, postGroupIndex, ErrorMessageSeverity.warning, [
    ...cardinalErrors,
    'profileRequired'
  ]);
};
